/*
 * Edits of «Формат и текст» save by themselves (spec 4.4): 1.5 s after the last
 * key and on leaving a field — never on every key. Every other action flushes
 * first, so it works on the saved version.
 */

export const AUTOSAVE_DELAY_MS = 1500;

/** idle: nothing changed yet; pending: a change waits for its save. */
export type AutosaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'conflict' | 'failed';

export interface Autosave {
  /** The text changed: save after the pause. */
  change(): void;
  /** The person left a field: save a pending change now. */
  blur(): Promise<AutosaveStatus>;
  /**
   * Before any other action: waits for a save in flight, then saves what is
   * still pending. 'idle' when there was nothing to save; 'conflict' or 'failed'
   * mean the action must not go on with the screen's text.
   */
  flush(): Promise<AutosaveStatus>;
  status(): AutosaveStatus;
  /** Status changes; returns the unsubscribe. */
  subscribe(fn: (status: AutosaveStatus) => void): () => void;
}

export function createAutosave(opts: {
  delayMs: number;
  /** One save of the text on the screen. A conflict means the screen reloads the server's version. */
  save: () => Promise<'saved' | 'conflict' | 'failed'>;
}): Autosave {
  let status: AutosaveStatus = 'idle';
  let dirty = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<AutosaveStatus> | null = null;
  /** A failed save got its one try by itself; a new change or a success gives it back. */
  let retried = false;
  const listeners = new Set<(s: AutosaveStatus) => void>();

  const set = (next: AutosaveStatus) => {
    if (next === status) return;
    status = next;
    for (const fn of listeners) fn(next);
  };
  const stopTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  const schedule = () => {
    stopTimer();
    timer = setTimeout(() => {
      timer = null;
      // A save in flight reschedules this change when it ends.
      if (!inFlight && dirty) void saveNow();
    }, opts.delayMs);
  };

  /** One save of what is pending now; a change made meanwhile stays pending. */
  function saveNow(): Promise<AutosaveStatus> {
    stopTimer();
    dirty = false;
    set('saving');
    inFlight = opts
      .save()
      .catch(() => 'failed' as const)
      .then((result) => {
        inFlight = null;
        if (result === 'failed') {
          // Kept for the next change, blur or flush; and tried once more by itself after the pause.
          dirty = true;
          if (!retried) {
            retried = true;
            schedule();
          }
        }
        if (result === 'saved') retried = false;
        if (result === 'conflict') {
          // The server's text replaces the screen's, edits made meanwhile included.
          dirty = false;
          stopTimer();
        }
        if (result === 'saved' && dirty) {
          set('pending');
          schedule();
        } else {
          set(result);
        }
        return result;
      });
    return inFlight;
  }

  const flush = async (): Promise<AutosaveStatus> => {
    let last: AutosaveStatus = 'idle';
    if (inFlight) last = await inFlight;
    if (!dirty || last === 'conflict') return last;
    return saveNow();
  };

  return {
    change() {
      dirty = true;
      retried = false;
      if (!inFlight) set('pending');
      schedule();
    },
    blur: () => (dirty || inFlight ? flush() : Promise.resolve<AutosaveStatus>('idle')),
    flush,
    status: () => status,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}
